import { useState, useEffect, useContext, useRef, act } from 'react';

import TaskNode from '../models/TaskNode.js'
import ItemClass from '../models/ItemClass.js';

import generateUUID from '../util/UUIDUtils.js';

import { DbContext, SortableContext } from '../App.js';

import ListItem from './ListItem/ListItem.js';
import Arrow from './Arrow.js';

import { Checkbox } from 'pretty-checkbox-react';


export default function TreeList({data, onDelete, onRefresh}) {

    const dbContext = useContext(DbContext)
    const db = dbContext.db


    function addTask(taskText) {

        const position = data.children.length
        const task = new ItemClass(generateUUID(), "root", taskText, position);
        const store = db.transaction("tasks", "readwrite").objectStore("tasks");

        store.add(task)

        store.transaction.oncomplete = () => {
            // console.log(task)
            dbContext.onChange(task)
        }
    }


    return (
        <div className='list-container'>
            <ListContainer items={data} onDelete={onDelete}/>
            <CreateTaskListItem 
                key = {generateUUID()} 
                taskNode={new TaskNode(new ItemClass(generateUUID(), null, "Create task", 1))}
                onAddTask = {addTask}/>
        </div>
    );
}

const ListContainer = ({items, onDelete}) => {

    const ref = useRef(null)
    const sortableContext = useContext(SortableContext)

    // --- Refs for values needed immediately inside event handlers ---
    // Using refs instead of state avoids the stale-closure bug where
    // onMouseMove reads isDragging=false right after onMouseDown sets it,
    // because React's re-render hasn't happened yet.
    const isDraggingRef = useRef(false)
    const activeRef = useRef(null)
    // Rects computed fresh on mousedown; stored in a ref so onMouseMove
    // reads them without triggering re-renders.
    const itemRectsRef = useRef([])

    // active still needs to be state so ListItem children re-render with
    // the correct active prop (used to suppress expand on the dragged item).
    const [active, setActive] = useState(null)
    const [over, setOver] = useState(null)
    const [indicator, setIndicator] = useState(null); // { top, left, width }

    useEffect(() => {
        if (active === null || !ref.current) return;
 
        const itemRects = [];
        ref.current.querySelectorAll('.list-item').forEach(item => {
            const rect = item.getBoundingClientRect();
            if (rect.height > 0) {   // skip items hidden inside display:none containers
                itemRects.push({
                    id:     item.getAttribute('data-id'),
                    top:    rect.top,
                    left:   rect.left,
                    bottom: rect.bottom,
                    right:  rect.right,
                });
            }
        });
        itemRectsRef.current = itemRects;
    }, [active]);

    const onMouseDown = (event) => {
        if (!ref.current) return;

        const px = event.clientX
        const py = event.clientY;

        // Compute drag-handle rects fresh right now — no stale cache.
        const dragHandleDoms = ref.current.querySelectorAll('.list-item-drag-handle');
        for (const el of dragHandleDoms) {
            const r = el.getBoundingClientRect();
            if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) {

                // Also snapshot item rects now for use in onMouseMove.
                const itemRects = [];
                ref.current.querySelectorAll('.list-item').forEach(item => {
                    const rect = item.getBoundingClientRect();
                    itemRects.push({
                        id:     item.getAttribute('data-id'),
                        top:    rect.top,
                        left:   rect.left,
                        bottom: rect.bottom,
                        right:  rect.right,
                    });
                });
                itemRectsRef.current = itemRects;

                // Update refs immediately — visible to onMouseMove
                // without waiting for a React re-render.
                isDraggingRef.current = true;
                activeRef.current = el.getAttribute('data-id');

                // Update state for rendering (async, but that's fine here).
                setActive(activeRef.current);
                sortableContext.onDragStart();
                return;
            }
        }
    }


    const onMouseMove = (event) => {
        // Read from ref, not state — guaranteed to be current even if
        // React hasn't re-rendered since onMouseDown fired.
        if (!isDraggingRef.current) return;

        const px = event.clientX;
        const py = event.clientY;

        for (const r of itemRectsRef.current) {
            console.log("item-rects size:", itemRectsRef.current.length)
            if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) {

                let position = "unknown"
                if (px > r.left + 50) {
                    position = "child"
                } else if (py < r.top + (r.bottom - r.top) / 2) {
                    position = "top"
                } else {
                    position = "bottom"
                }

                const y = py < r.top + (r.bottom - r.top) / 2 ? r.top - 3 : r.bottom + 3;
                const x = px > r.left + 50 ? r.left + 50 : r.left;
                setIndicator({ top: y, left: x, width: r.right - r.left });
                setOver(r.id);

                sortableContext.onDragOver({
                    active: activeRef.current, // use ref, not stale state
                    over: r.id,
                    position: position
                });
                return;
            }
        }
        setIndicator(null);
    }


    const onMouseUp = (event) => {
        isDraggingRef.current = false;
        activeRef.current = null;
        itemRectsRef.current = [];

        setIndicator(null);
        setActive(null);
        setOver(null);

        sortableContext.onDragEnd();
    }

    const onMouseLeave = (event) => {
//        isDraggingRef.current = false)
//        console.log("onDragEnd", event)
    }

    return (
        // <div ref={setNodeRef} style={{color: isOver? 'green' : 'black'}}> {listItems} </div>
        <div ref={ref} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseLeave}> 
            {items.children
                .filter((element) => element.value.isVisible)
                .map (
                    (element) => 
                        <ListItem 
                            key = {element.value.id} 
                            taskNode={element} 
                            deleteTask={(taskId) => { onDelete(taskId)}} 
                            onDragFinished={() => {}}
                            active={active}
                        />
                )
            }
            {indicator && (
                <div style={{
                    position: 'fixed',
                    height: 2.5,
                    background: '#000',
                    pointerEvents: 'none',
                    top: indicator.top,
                    left: indicator.left,
                    width: indicator.width,
                    transition: 'top 0.08s ease',
                }} />
            )}

        </div>
    )

}


const CreateTaskListItem = ({ taskNode, onAddTask }) => {

    const db = useContext(DbContext);

    const [node, setNode] = useState(taskNode)
    const [task, setTask] = useState(taskNode.value)
    const [text, setText] = useState(task.data);
    const [isExpanded, setIsExpanded] = useState(task.isExpanded ?? false);
    const [taskText, setTaskText] = useState(taskNode.value.text)


    const handleTextChange = (event) => {
        // Logic to handle text change
        if (event.code === "Enter") {
            onAddTask(taskText)
            setText(task.data)
            event.preventDefault();
        }
    };


    const inputRef = useRef(null)
    const [isFocused, setIsFocused] = useState(false)

    function onFocus() {
        setIsFocused(true)
        setText("");
        takeCursorToZero();
    }

    function takeCursorToZero() {

        const element = inputRef.current
        element.focus()
        const range = document.createRange();
        const selection = window.getSelection();
        
        range.setStart(element, 0);
        range.collapse(true);
        
        selection.removeAllRanges();
        selection.addRange(range);

        console.log(range)
    }


    return (
        <div className="list-item-container" id={task.id}>
            <div className="list-item">
                <Arrow doesHaveChildren={ node.children.length > 0 } isExpanded={isExpanded}/>
                <Checkbox readOnly className="list-item-checkbox" checked={false} locked={true} style={{marginRight: '2px', marginLeft: '2px'}} shape="curve" variant="fill" animation="smooth" icon={<span className="material-icons" style={{ verticalAlign: 'middle' }}>check</span>} ></Checkbox>
                
                <span className='list-item-text-area'>
                    <span style={{opacity: 0.5}} ref={inputRef} className="list-item-text" contentEditable={true} onKeyDown={handleTextChange} onClick={onFocus} onFocus={onFocus} onBlur={() => setText(task.data)}onInput={(event) => { setTaskText(event.target.textContent)} } suppressContentEditableWarning={true}>{text}</span>
                </span>
            </div>
        </div>
    );

};